<template>
  <div
    class="selector"
    role="radiogroup"
    :aria-label="ariaLabel || t('segmentedControl.selectOptionAria')"
  >
    <div
      :style="{ cursor: elements.length == 1 ? 'default' : loading ? 'not-allowed' : 'pointer' }"
      :class="['elements', { wrapped: isWrappedLayout }]"
    >
      <div
        v-if="elements.length > 1 && !isWrappedLayout"
        class="active"
        :style="{ left: leftAmount, width: 100 / elements.length + '%' }"
      ></div>
      <button
        v-for="(element, index) in elements"
        :key="index"
        @click="change(element)"
        @keydown="handleKeyDown($event, element, index)"
        :style="{
          'pointer-events': elements.length == 1 || loading ? 'none' : 'auto',
          'background-color': elements.length == 1 ? 'var(--gray-color)' : '',
          width: buttonWidth(index),
        }"
        :class="{
          activeText: selectedId == element[elementId] && elements.length != 1,
          wrappedActive: selectedId == element[elementId] && isWrappedLayout,
        }"
        :disabled="elements.length == 1 || loading"
        :aria-checked="selectedId == element[elementId] ? 'true' : 'false'"
        :aria-label="`${element[elementName]}${elements.length == 1 ? ` ${t('segmentedControl.only')}` : ''}`"
        role="radio"
        :tabindex="selectedId == element[elementId] ? 0 : -1"
      >
        {{ element[elementName] }}
        {{ elements.length == 1 ? t('segmentedControl.only') : '' }}
      </button>
    </div>
  </div>
</template>

<script>
import i18n from '@/plugins/i18n'

export default {
  props: ['elements', 'elementId', 'selectedId', 'elementName', 'loading', 'ariaLabel'],
  computed: {
    isWrappedLayout() {
      return this.elements.length > 4
    },
    firstRowCount() {
      return Math.ceil(this.elements.length / 2)
    },
    secondRowCount() {
      return this.elements.length - this.firstRowCount
    },
    leftAmount() {
      let elementsLength = this.elements.length
      let multiplier = 100 / elementsLength
      let selectedIndex = this.elements.findIndex((element) => element[this.elementId] == this.selectedId)
      let leftAmount = multiplier * selectedIndex
      return leftAmount + '%'
    },
  },
  methods: {
    t(key, params = {}) {
      return i18n.global.t(key, params)
    },
    buttonWidth(index) {
      if (!this.isWrappedLayout) {
        return 100 / this.elements.length + '%'
      }

      if (index < this.firstRowCount) {
        return 100 / this.firstRowCount + '%'
      }

      return 100 / this.secondRowCount + '%'
    },
    change(element) {
      this.$emit('change', element)
    },
    handleKeyDown(event, element, index) {
      if (this.elements.length === 1 || this.loading) return

      let newIndex = index
      const isRTL = document.dir === 'rtl'

      // Arrow key navigation
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault()
        newIndex = isRTL ? index - 1 : index + 1
        if (newIndex >= this.elements.length) newIndex = 0
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault()
        newIndex = isRTL ? index + 1 : index - 1
        if (newIndex < 0) newIndex = this.elements.length - 1
      } else if (event.key === 'Home') {
        event.preventDefault()
        newIndex = 0
      } else if (event.key === 'End') {
        event.preventDefault()
        newIndex = this.elements.length - 1
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.change(element)
        return
      } else {
        return
      }

      // Change to the new element and focus it
      const newElement = this.elements[newIndex]
      this.change(newElement)

      // Focus the newly selected element
      this.$nextTick(() => {
        const buttons = event.target.parentElement.querySelectorAll('button')
        if (buttons[newIndex]) {
          buttons[newIndex].focus()
        }
      })
    },
  },
}
</script>

<style src="../../assets/css/Elements/SegmentedControl.css" scoped></style>
